import Grid from '@material-ui/core/Grid';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import classNames from 'classnames';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';

import Button from '~/ui/components/common/Button';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import ListItem from '~/ui/components/common/ListItem';
import ProfilePhotoUpload from '~/ui/components/common/ProfilePhotoUpload';

import ChangePasswordForm from '../ChangePassword/components/ChangePasswordForm';
import ChangeRole from './ChangeRole';

import useRole from '~/store/user/hooks/useRole';
import { extractErrorMessage } from '~/utils/error/error';
import { isFile } from '~/utils/file';
import { getSubRoleName } from './helper';

import { EDIT_PROFILE, FRESH_DESK_SUPPORT } from '~/ui/constants/paths';
import { IChangePasswordFormValues } from './types';

import editWhite from '~/ui/assets/images/editWhite.svg';
import styles from './Profile.module.scss';

const Profile = (): ReactElement => {
  const navigate = useNavigate();

  const current = useStoreState(state => state.user.current);
  const [showConfirm, setShowConfirm] = useState<'profile' | 'logout'>(null);
  const [value, setValue] = useState<File | string | null>(current.photo || null);
  const [error, setError] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  const [changingPassword, setChangingPassword] = useState(false);

  const { isSuperAdmin, isProductOwner } = useRole();

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const { onLogout, onUploadAvatar, onRemoveAvatar } = useStoreActions(actions => actions.user);
  const onChangePassword = useStoreActions(actions => actions.user.onChangePassword);
  const { setChannels, resetMessages } = useStoreActions(actions => actions.coordinate);
  const setNetworkActiveTab = useStoreActions(state => state.network.setNetworkActiveTab);
  const onGetCurrentProfile = useStoreActions(actions => actions.user.onGetCurrentProfile);

  const isLogout = showConfirm === 'logout';
  const description = isLogout
    ? 'Are you sure you want to log out?'
    : 'Are you sure you want to remove profile photo?';
  const confirmText = isLogout ? 'Log Out' : 'Remove';

  const onUpload = async (file: File) => {
    try {
      setLoadingPhoto(true);
      if (!isFile(file)) return;

      await onUploadAvatar(file);
      await onGetCurrentProfile();
      setValue(file);
      showNotify({ message: 'Photo successfully updated' });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoadingPhoto(false);
    }
  };

  const onRemove = async () => {
    try {
      setLoadingPhoto(true);
      await onRemoveAvatar();
      setValue('');
      showNotify({ message: 'Photo successfully removed!' });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoadingPhoto(false);
    }
  };

  const onSubmitChangePassword = async (values: IChangePasswordFormValues, cb: () => void) => {
    try {
      setChangingPassword(true);

      const payload = { email: current?.email?.trim(), ...values };
      await onChangePassword(payload);
      cb?.();

      showNotify({ message: 'Password successfully changed!' });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setChangingPassword(false);
    }
  };

  const onConfirm = isLogout
    ? () => {
        onLogout();
        setNetworkActiveTab(0);
        resetMessages();
        setChannels([]);
      }
    : () => {
        onRemove();
        setShowConfirm(null);
      };

  const openSupportLink = () => {
    window.open(FRESH_DESK_SUPPORT, '_blank');
  };

  const onMount = useCallback(async () => {
    try {
      await onGetCurrentProfile();
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  }, [onGetCurrentProfile, showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <div>
      <div className={styles.header}>
        <h2>Your Profile</h2>
        {isSuperAdmin && (
          <Button
            color="primary"
            variant="contained"
            startIcon={<img src={editWhite} alt="edit" />}
            onClick={() => navigate(EDIT_PROFILE)}
          >
            Edit
          </Button>
        )}
      </div>
      <Grid container spacing={3}>
        <Grid item sm={5}>
          <div className={styles.itemsWrapper}>
            <div className={styles.item}>
              <h3 className={styles.header}>Profile Details</h3>
            </div>
            <ListItem label="First Name" content={current?.firstName} />
            <ListItem label="Last Name" content={current?.lastName} />
            {!isSuperAdmin && (
              <ListItem label="Sub-Role" content={getSubRoleName(current?.subRoles)} />
            )}
            <ListItem label="Phone" content={current?.phone} />
            <ListItem label="Email" content={current?.email} />
          </div>
        </Grid>
        <Grid item sm={7}>
          {!isSuperAdmin && !isProductOwner && (
            <div className={styles.uploaderWrapper}>
              <ProfilePhotoUpload
                disabled={loadingPhoto}
                value={value}
                onUpload={onUpload}
                onError={setError}
                onRemove={() => setShowConfirm('profile')}
              />
              {error && <div>{error}</div>}
            </div>
          )}
        </Grid>
        <ChangeRole />
        <ChangePasswordForm
          loading={changingPassword}
          buttonFullWidth={false}
          error=""
          title="Change Password"
          isPasswordExpired={false}
          buttonText="Change password"
          onSubmit={onSubmitChangePassword}
          formClassName={styles.changePasswordForm}
          className={classNames(styles.itemsWrapper, styles.changePassword)}
        />
      </Grid>
      <div className={styles.buttonContainer}>
        <Button
          type="button"
          color="primary"
          variant="contained"
          startIcon={<ExitToAppIcon />}
          onClick={() => setShowConfirm('logout')}
        >
          Log Out
        </Button>
        <Button
          type="button"
          color="primary"
          variant="outlined"
          startIcon={<InfoIcon />}
          onClick={openSupportLink}
        >
          Submit a Ticket
        </Button>
        {process.env.REACT_APP_VERSION && (
          <>
            <br />
            <br />
            <span>App Version: {process.env.REACT_APP_VERSION}</span>
          </>
        )}
      </div>
      {showConfirm && (
        <ConfirmModal
          confirmText={confirmText}
          description={description}
          onConfirm={() => {
            onConfirm();
          }}
          onClose={() => setShowConfirm(null)}
        />
      )}
    </div>
  );
};

export default Profile;
